import React, { ComponentProps } from "react";
import { useState, useEffect } from "react";

import {
    Image,
    StyleSheet,
    Text,
    View,
    TouchableOpacity,
    ScrollView,
    Platform,
    Dimensions
} from 'react-native';

import { EventRegister } from "../../../shared/utils/EventListeners";

import AppTheme from "../../../shared/utils/Theme";
import * as Constants from "../../../shared/utils/Constants";
import MainOptions from "../../../shared/models/MainOptions";

// UI
import Images from "../../utils/Images";
import Style from "../../../shared/utils/Style";

// SVG
import SVGView from "../../../shared/utils/SvgView";
import { default as icBack } from "../../../shared/assets/svg/icons/solid/angle-left.svg";
import { default as icMenu } from "../../../shared/assets/svg/icons/solid/home.svg";
import { default as icDismiss } from "../../../shared/assets/svg/icons/solid/times.svg";

import { isUserATeacher } from "../../../shared/utils/LocalStorage";

import * as Navigation from "../../utils/Navigation/Navigation";


import { useNavigate, useLocation } from "react-router-dom";

// Component
import About from "../../../shared/components/About/About";
import UserProfile from "../../../shared/components/Profile/UserProfile";
import ConsentNotification from "../../../shared/components/Legal/ConcentNotification";
import Mood from "../../../shared/components/Mood/Mood";
import ActivityForm from "../../../shared/components/Admin/ActivityForm";
import Subscription from "../../../shared/components/Subscription/Subscription";
import LegalMentions from "../../../shared/components/Legal/LegalMentions";
import SuperRoom from "../../../shared/components/Rooms/SuperRoom";

import ActivityIndicator from "../../../shared/designSystem/ActivityIndicator/ActivityIndicator";


import { replaceCustomVariablesInString } from "../../../shared/utils/StringUtils";
import MainStyle from "../../../shared/styles/game/MainStyle";
import User from "../../../shared/data/user/User";
import CategoryHomeStyle from "../../../shared/styles/categories/CategoryHomeStyle";
import i18n from "../../../shared/utils/Localization/Localization";
import Modal from "../Modal/Modal";
import { AppTarget, getAppTarget } from "../../utils/Navigation/Host";
import ActivityPlaylist from "../../../shared/screens/activities/ActivityPlaylist";
import ActivitySheetScreen from "../../../shared/screens/activities/ActivitySheetScreen";
import ActivitySheet from "../../../shared/components/Activities/ActivitySheet";
import { useSpecificSize } from "../../Theme/SpecificSize";
import SectionCategories from "../../../shared/components/Section/SectionCategories";
import CategoryHome from "../../../shared/components/Categories/CategoryHome";
import ActivityMediaSheet from "../../../shared/components/Activities/ActivityMediaList/ActivityMediaSheet";
import ActivityMediaList from "../../../shared/components/Activities/ActivityMediaList/ActivityMediaList";
import SubscriptionPaywall from "../../../shared/screens/premium/SubscriptionPaywall";
interface MainProps {
    mainOptions: MainOptions,
    children: any
}

type ModalOption = {
    name: string;
    passProps?: any;
}

const Main = (props: MainProps) => {

    const appTheme: AppTheme = new AppTheme();
    const { mainOptions, children } = props;

    const {
        fullScreenWidth,
        fullScreenHeight,
        availableScreenSize,
        contentSize,
        sideMenuSize,
        horizontalSpacing
    } = useSpecificSize();

    const {
        componentId = "",
        canGoBack = false,
        showNavBar = false,
        canBeDismissed = false,
        canBeHidden = false,
        title = "",
        rightButtonIcon = undefined,
        rightButtonAction = undefined,
        forceFullWidth = false,
        simpleRender = false } = mainOptions;

    const mainBackgroundColor = ((mainOptions.backgroundColor !== undefined) && (mainOptions.backgroundColor.length > 0)) ? mainOptions.backgroundColor : appTheme.backgroundColor;
    const navigationColor = ((mainOptions.navigationColor !== undefined) && (mainOptions.navigationColor.length > 0)) ? mainOptions.navigationColor : appTheme.navigationColor;

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const images: Images = new Images;
    const mainStyle: MainStyle = new MainStyle();
    const categoryHomeStyle: CategoryHomeStyle = new CategoryHomeStyle();
    const user: User = User.getInstance();

    const [modalOptions, setModalOptions] = useState<ModalOption[]>([])
    const [modalIsVisible, setModalIsVisible] = useState(false);

    const navBarHeightWithoutTop = appTheme.pixelPerfect(54);
    const navBarHeight = navBarHeightWithoutTop;

    let navigate = useNavigate();
    let location = useLocation();

    // ComponentDidMount
    useEffect(() => {
        const showModalListener = EventRegister.addEventListener(Constants.EventShowModal, (item: ModalOption) => {
            setModalOptions(prev => [...prev, item]);
        });
        const dismissModalListener = EventRegister.addEventListener(Constants.EventDismissmodal, () => {
            setModalOptions(prev => prev.slice(0, prev.length - 1));
        });
        return () => {
            EventRegister.removeEventListener(dismissModalListener);
            EventRegister.removeEventListener(showModalListener);
        };
    }, []);

    useEffect(() => {
        if (modalOptions.length === 0) {
            setModalIsVisible(false);
        } else {
            setModalIsVisible(true);
        }
    }, [modalOptions]);

    const goBack = () => {
        Navigation.pop({
            componentId,
            navigate
        });
    }

    const goHome = () => {
        if (user.profile === "teacher") {
            navigate(Constants.ScreenAppMenu + "/" + Constants.CONTEXT.teacher);
        } else {
            navigate(Constants.ScreenAppMenu + "/" + Constants.CONTEXT.family);
        }
    }

    const dismissModal = () => {
        const { canBeHidden = false } = mainOptions;
        if (canBeHidden === true) {
            Navigation.dismissModalInView({
                componentId
            });
        } else {
            Navigation.dismissModal({
                componentId,
                navigate
            });
        }
    }

    // Nouvelle navbar
    const getLeftButton = () => {
        // Si on est en mobile, on a un bouton retour
        // En web un bouton nous ramenant au menu
        const buttonSize = Platform.OS === "web" ? appTheme.pixelPerfect(50) : appTheme.pixelPerfect(30);
        const rightButtonWidth = appTheme.pixelPerfect(120);
        let imageForLeftButton = images.icLiliAtSchoolCircle;
        if (user.profile === "family") {
            imageForLeftButton = images.icLiliAtHomeCircle;
        } else if (user.profile === "animator") {
            imageForLeftButton = images.icLiliAtExtracurricularCircle;
        }
        const leftButtonContent = <Image source={imageForLeftButton} style={{ width: buttonSize, height: buttonSize }} />
        return <TouchableOpacity onPress={goBack} style={[{ width: rightButtonWidth, height: buttonSize, justifyContent: "center", alignItems: "center" }]}>
            <View style={[mainStyle.shadowed, { borderRadius: buttonSize / 2 }]}>
                {leftButtonContent}
            </View>
        </TouchableOpacity>
    }

    const goToSuperRoom = () => {
        Navigation.showModalInView({
            componentId,
            name: Constants.ScreenSuperRoom
        });
    }

    const getRightButton = () => {
        const imageSize = appTheme.pixelPerfect(40);
        const rightButtonWidth = appTheme.pixelPerfect(120);
        const buttonTitle = user.profile === "teacher" ? i18n.t("appMenu.teachers.button.title") : user.profile === "animator" ? i18n.t("appMenu.animators.button.title") : i18n.t("appMenu.parents.button.title");
        const buttonImage = user.profile === "teacher" ? images.icSuperTeachersRoom : images.icSuperParentsRoom;
        const paddingBottom = appTheme.bottomInsets > 0 ? appTheme.bottomInsets : appTheme.pixelPerfect(20);
        return <TouchableOpacity onPress={goToSuperRoom} style={{ width: rightButtonWidth, justifyContent: "space-between", alignItems: "center", height: appTheme.pixelPerfect(50) }}>
            <View style={[mainStyle.shadowed, { borderRadius: imageSize / 2 }]}>
                <Image source={buttonImage} style={{ width: imageSize, height: imageSize }} resizeMode="cover" />
            </View>
            <Text style={{ fontFamily: appTheme.primaryBoldFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(3), textAlign: "center" }}>{buttonTitle}</Text>
        </TouchableOpacity>
    }

    const getNavBar = () => {
        if (showNavBar === false) {
            return <View />;
        }
        const titleToDisplay = replaceCustomVariablesInString(title);
        return <View style={[mainStyle.shadowed, categoryHomeStyle.navigationBar(appTheme.white)]}>
            <View style={categoryHomeStyle.navigationBarContent}>
                {getLeftButton()}
                <Text numberOfLines={2} style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: "Lili", fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center", color: Platform.OS === "web" ? appTheme.darkBlue : clr.txt }}>{titleToDisplay.replace("\\n", "\n")}</Text>
                {getRightButton()}
            </View>
        </View>
    }

    let rightAbsoluteButton = <View />;
    if (showNavBar === false) {
        if ((canBeDismissed === true) || (canBeHidden === true)) {
            rightAbsoluteButton = <TouchableOpacity onPress={dismissModal} style={{ position: "absolute", top: appTheme.pixelPerfect(5), right: appTheme.pixelPerfect(5), marginBottom: appTheme.pixelPerfect(5), backgroundColor: appTheme.white, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(20), justifyContent: "center", alignItems: "center" }}>
                <SVGView Component={icDismiss} width={appTheme.pixelPerfect(20)} height={appTheme.pixelPerfect(20)} color={navigationColor} />
            </TouchableOpacity>;
        } else if ((rightButtonIcon !== undefined) && (rightButtonAction !== undefined)) {
            rightAbsoluteButton = <TouchableOpacity onPress={rightButtonAction} style={{ position: "absolute", top: appTheme.pixelPerfect(5), right: appTheme.pixelPerfect(5), marginBottom: appTheme.pixelPerfect(5), backgroundColor: appTheme.white, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(20), justifyContent: "center", alignItems: "center" }}>
                <SVGView Component={rightButtonIcon} size={appTheme.pixelPerfect(20)} color={navigationColor} />
            </TouchableOpacity>;
        }
    }

    let leftAbsoluteButton = <View />;
    if (showNavBar === false) {
        if (canGoBack === true) {
            leftAbsoluteButton = <TouchableOpacity onPress={goBack} style={{ position: "absolute", top: appTheme.pixelPerfect(5), left: appTheme.pixelPerfect(5), marginBottom: appTheme.pixelPerfect(5), backgroundColor: appTheme.white, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(20), justifyContent: "center", alignItems: "center" }}>
                <SVGView Component={icBack} width={appTheme.pixelPerfect(20)} height={appTheme.pixelPerfect(20)} color={navigationColor} />
            </TouchableOpacity>;
            if (location.state === "deeplink") {
                leftAbsoluteButton = <TouchableOpacity
                    onPress={goHome}
                    style={{ position: "absolute", top: appTheme.pixelPerfect(5), left: appTheme.pixelPerfect(5), marginBottom: appTheme.pixelPerfect(5), backgroundColor: appTheme.white, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(20), justifyContent: "center", alignItems: "center" }}>
                    <SVGView Component={icBack} size={appTheme.pixelPerfect(24)} color={navigationColor} />
                </TouchableOpacity>;
            }
        }
    }

    const getBackground = () => {
        const position = Platform.OS === "web" ? "fixed" : "absolute";
        return (<View style={{ position, top: 0, left: 0, width: appTheme.getFullScreenWidth(), height: "100%", backgroundColor: appTheme.schoolColor }} />);

    }

    const getMainViewStyle = () => {
        const marginTop: number = ((mainOptions.showMenu === true) && (mainOptions.showNavBar === true)) ? 60 : 0;
        const childrenCointainerWidth = appTheme.getFullAppWidth();
        let mainViewStyle: any[] = [{ justifyContent: "center", alignItems: "center", width: childrenCointainerWidth, marginTop, flex: 1 }];
        return mainViewStyle;
    }

    const getModalContentAllViewsStacked = () => {
        if ((modalIsVisible === true) && (modalOptions.length > 0)) {
            let modalViews: React.JSX.Element[] = [];
            const aModalOption = modalOptions[modalOptions.length - 1];
            for (const aModalOption of modalOptions) {
                let paddingLeft: number = 0;
                let componentToDisplay: React.JSX.Element | null = null;
                const props = aModalOption.passProps;
                if (aModalOption.name === Constants.ScreenAbout) {
                    componentToDisplay = <About {...props} />
                } else if (aModalOption.name === Constants.ScreenUserProfile) {
                    componentToDisplay = <UserProfile {...props} />
                } else if (aModalOption.name === Constants.ScreenConsentNotification) {
                    componentToDisplay = <ConsentNotification {...props} />
                } else if (aModalOption.name === Constants.ScreenMood) {
                    componentToDisplay = <Mood {...props} />
                } else if (aModalOption.name === Constants.ScreenActivityForm) {
                    componentToDisplay = <ActivityForm {...props} />
                } else if (aModalOption.name === Constants.ScreenSubscription) {
                    componentToDisplay = <Subscription {...props} />
                } else if (aModalOption.name === Constants.ScreenLegalMentions) {
                    componentToDisplay = <LegalMentions {...props} />
                } else if (aModalOption.name === Constants.ScreenSuperRoom) {
                    componentToDisplay = <SuperRoom {...props} />
                } else if (aModalOption.name === Constants.ScreenActivityPlaylist) {
                    componentToDisplay = <ActivityPlaylist {...props} />
                } else if (aModalOption.name === Constants.ScreenActivitySheetScreen) {
                    componentToDisplay = <ActivitySheetScreen {...props} />
                } else if (aModalOption.name === Constants.ScreenActivitySheet) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <ActivitySheet inModal={true} {...props} />
                } else if (aModalOption.name === Constants.ScreenSectionCategories) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <SectionCategories isModal={true} {...props} />
                } else if (aModalOption.name === Constants.ScreenCategoryHome) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <CategoryHome isModal={true} {...props} />
                } else if (aModalOption.name === Constants.ScreenActivityMediaSheet) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <ActivityMediaSheet isModal={true} {...props} />
                } else if (aModalOption.name === Constants.ScreenActivityMediaList) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <ActivityMediaList isModal={true} {...props} />
                } else if (aModalOption.name === Constants.ScreenSubscriptionPaywall) {
                    paddingLeft = sideMenuSize;
                    componentToDisplay = <SubscriptionPaywall isModal={true} {...props} />
                }

                let overlayView: React.JSX.Element | null = null;
                if (modalViews.length === (modalOptions.length - 1)) {
                    overlayView = <View style={{ backgroundColor: "#FFFFFFAA", width: innerWidth, height: innerHeight, position: "fixed" }} />;
                }
                modalViews.push(<View style={{ position: "absolute", top: 0, left: 0 }}>
                    {overlayView}
                    <View style={{ width: innerWidth, alignItems: "center", paddingLeft }}>
                        {componentToDisplay}
                    </View>
                </View>)
            }
            return modalViews;
        }
        return <View />
    }

    const getModalContent = () => {
        console.log({modalIsVisible});
        if ((modalIsVisible === true) && (modalOptions.length > 0)) {
            let modalViews: React.JSX.Element[] = [];
            const aModalOption = modalOptions[modalOptions.length - 1];
            let paddingLeft: number = 0;
            let componentToDisplay: React.JSX.Element | null = null;
            const props = aModalOption.passProps;
            if (aModalOption.name === Constants.ScreenAbout) {
                componentToDisplay = <About {...props} />
            } else if (aModalOption.name === Constants.ScreenUserProfile) {
                componentToDisplay = <UserProfile {...props} />
            } else if (aModalOption.name === Constants.ScreenConsentNotification) {
                componentToDisplay = <ConsentNotification {...props} />
            } else if (aModalOption.name === Constants.ScreenMood) {
                componentToDisplay = <Mood {...props} />
            } else if (aModalOption.name === Constants.ScreenActivityForm) {
                componentToDisplay = <ActivityForm {...props} />
            } else if (aModalOption.name === Constants.ScreenSubscription) {
                componentToDisplay = <Subscription {...props} />
            } else if (aModalOption.name === Constants.ScreenLegalMentions) {
                componentToDisplay = <LegalMentions {...props} />
            } else if (aModalOption.name === Constants.ScreenSuperRoom) {
                componentToDisplay = <SuperRoom {...props} />
            } else if (aModalOption.name === Constants.ScreenActivityPlaylist) {
                componentToDisplay = <ActivityPlaylist {...props} />
            } else if (aModalOption.name === Constants.ScreenActivitySheetScreen) {
                componentToDisplay = <ActivitySheetScreen {...props} />
            } else if (aModalOption.name === Constants.ScreenActivitySheet) {
                paddingLeft = sideMenuSize;
                componentToDisplay = <ActivitySheet inModal={true} {...props} />
            } else if (aModalOption.name === Constants.ScreenSectionCategories) {
                paddingLeft = sideMenuSize;
                componentToDisplay = <SectionCategories isModal={true} {...props} />
            } else if (aModalOption.name === Constants.ScreenCategoryHome) {
                paddingLeft = sideMenuSize;
                componentToDisplay = <CategoryHome isModal={true} {...props} />
            } else if (aModalOption.name === Constants.ScreenActivityMediaSheet) {
                paddingLeft = sideMenuSize;
                componentToDisplay = <ActivityMediaSheet isModal={true} {...props} />
            } else if (aModalOption.name === Constants.ScreenActivityMediaList) {
                paddingLeft = sideMenuSize;
                componentToDisplay = <ActivityMediaList isModal={true} {...props} />
            } else if (aModalOption.name === Constants.ScreenSubscriptionPaywall) {
                console.log({ aModalOption });
                paddingLeft = sideMenuSize;
                componentToDisplay = <SubscriptionPaywall isModal={true} {...props} />
            }
            let blueOverlay: React.JSX.Element = <View style={{ position: "fixed", width: innerWidth - sideMenuSize, height: innerHeight, top: 0, left: sideMenuSize }}>
                <View style={{ position: "sticky", backgroundColor: appTheme.darkBlue, width: innerWidth - sideMenuSize, height: innerHeight }} />
            </View>
            let overlayView: React.JSX.Element = <View style={{ backgroundColor: "#FFFFFFAA", width: innerWidth, height: innerHeight, position: "fixed" }} />;
            return <View key={new Date().getTime().toString()} style={{ position: "absolute", top: 0, left: 0 }}>
                {blueOverlay}
                {overlayView}
                <View style={{ width: innerWidth, alignItems: "center", paddingLeft }}>
                    {componentToDisplay}
                </View>
            </View>;
        }
        return <View />
    }

    const getChildrenContent = () => {
        if (isLoading === true) {
            return <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator color={appTheme.talk} />
            </View>
        }

        // Si on a une barre de navigation, on doit décaler la vue
        const marginTop: number = showNavBar === true ? navBarHeight : 0;
        return <View style={[getMainViewStyle(), { backgroundColor: mainBackgroundColor, marginTop, paddingBottom: marginTop }]}>
            {children}
            <Modal
                transparent={true}
                visible={modalIsVisible}
                animationType="slide">
                <View style={{ height: innerHeight }}>
                    {getModalContent()}
                </View>
            </Modal>
            {rightAbsoluteButton}
            {leftAbsoluteButton}
        </View>;
    }

    const paddingTop: number = showNavBar === true ? appTheme.pixelPerfect(50) : 0;
    let mainwidth: number = ((user.currentUser === null) || (forceFullWidth === true)) ? appTheme.getFullScreenWidth() : appTheme.getFullAppWidth();
    const appTarget: AppTarget = getAppTarget();
    if ((appTarget !== "public") && (appTarget !== "lhq")) {
        mainwidth = appTheme.getFullAppWidth();
    }
    if (simpleRender === true) {
        return <View>
            {children}
            <Modal
                transparent={true}
                visible={modalIsVisible}
                animationType="slide">
                <View style={{ height: innerHeight }}>
                    {getModalContent()}
                </View>
            </Modal>
        </View>
    }
    return (
        <View style={[styles.main, { width: mainwidth, paddingTop, height: appTheme.getFullAppHeight() }]}>
            {getBackground()}
            {getChildrenContent()}
            {getNavBar()}
        </View>
    )
}

export default Main;

const styles = StyleSheet.create({
    main: {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start"
    },
    navBar: {
        position: 'absolute',
        flexDirection: "row",
        alignItems: "flex-end"
    },
    navBarShadow: {
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,

        elevation: 5,
    },
    webViewShadow: {
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,

        elevation: 5,
    }
});
